/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCountryCallingCode, type CountryCode } from "libphonenumber-js";

import { cssFns } from "@superweb/css";
import {
  createComboBoxState,
  createDateFieldState,
  createPhoneFieldState,
  createTextFieldState,
  ExperimentalFormRow,
  PhoneField,
  TextField,
  useDebouncedState,
  useTypo,
  useUiColors,
  type ComboBoxOption,
  type ComboBoxState,
  type PhoneFieldState,
  type TextFieldState,
} from "@superweb/ui";
import { useLocale } from "@superweb/intl";
import { useFlag } from "@superweb/flags";

import { useAction, type ApiResponse } from "@fleet/api";
import { useOnDemandV1StationsGet } from "@fleet/api/fleet-forms";

import { AddressSuggest, FormSuggest, PhoneVerificator } from "#internal/ui";
import { Message, useMessage } from "#internal/intl";
import {
  useErrorMessages,
  useOnChangePartial,
  useOnShowErrors,
  useSubmitErrorMessages,
  useValidate,
} from "#internal/utils";
import { useLogAction } from "#internal/analytics";

import {
  RegionDetailsFields,
  type RegionDetailsState,
} from "../region-details-fields";
import { useGetSteps } from "../steps";

import { RegisterForm } from "./form";
import {
  DriverLicenseDetailsFields,
  type DriverLicenseDetailsState,
} from "../driver-license-details-fields";

type PersonalFormState = {
  phone: PhoneFieldState;
  first_name: TextFieldState;
  last_name: TextFieldState;
  address: ComboBoxState<ComboBoxOption>;
  address_apartment: TextFieldState;
  doc_address: ComboBoxState<ComboBoxOption>;
  tax_identification_number: TextFieldState;
  tax_system: ComboBoxState<ComboBoxOption>;
  email: TextFieldState;
  tax_authority_code: TextFieldState;
  station_id: ComboBoxState<ComboBoxOption>;
};

type FormRequirementItem = "required" | "optional" | "none";
type FormRequirements = {
  license: FormRequirementItem;
  vehicle: FormRequirementItem;
  region: FormRequirementItem;
};

export const SingleFlowContent = ({
  refId,
  parkInfo,
  sessionId,
  isDelivery,
  formErrorMessage,
  setFormErrorMessage,
  profession,
  employmentType,
  isIframe,
}: {
  refId: string;
  sessionId: string;
  isDelivery: boolean;
  formErrorMessage?: string;
  setFormErrorMessage: (errorMessage: string) => void;
  parkInfo: Extract<
    ApiResponse<"/fleet-forms/selfreg/v1/park/info", "GET">,
    { status: 200 }
  >["body"];
  profession?: {
    profession_id: string;
    employment_type_list?: {
      employment_type: string;
      required_fields?: string[];
      optional_fields?: string[];
    }[];
    form_requirements: FormRequirements;
  };
  employmentType?: "selfemployed" | "individual_entrepreneur" | "park_employee";
  isIframe: boolean;
}) => {
  const message = useMessage();
  const locale = useLocale();
  const errorMessages = useErrorMessages();
  const typo = useTypo();
  const uiColors = useUiColors();
  const flag = useFlag();
  const logActionSubmit = useLogAction("submit");
  const logActionInvalid = useLogAction("invalid");
  const navigate = useNavigate();

  const getSteps = useGetSteps();
  const { nextStep } = getSteps({
    formRequirements:
      profession?.form_requirements ?? parkInfo.form_requirements,
    currentStep: "register",
    employmentType,
  });

  const hasLicenseSection =
    parkInfo.form_requirements.license !== "none" &&
    employmentType !== "park_employee";
  const hasRegionSection = parkInfo.form_requirements.region !== "none";

  const enablePhoneVerification = Boolean(parkInfo.phone_verification);

  const enableTestPhones = flag({
    id: "7c942967-cab9-46e2-9d06-729692689aa0",
    description: "Enable test phones for testing",
  });

  const currentProfession = profession
    ? parkInfo.profession_settings_list.find(
        (item) => item.profession_id === profession.profession_id,
      )
    : undefined;
  const currentEmploymentType = currentProfession?.employment_type_list?.find(
    (item) => item.employment_type === employmentType,
  );

  const optionalFields =
    currentEmploymentType?.optional_fields ?? parkInfo.optional_fields;
  const requiredFields =
    currentEmploymentType?.required_fields ?? parkInfo.required_fields;

  const additionalFieldsMap = {
    ...optionalFields?.reduce<Record<string, { required: boolean }>>(
      (res, item) => ({
        ...res,
        [item]: { required: false },
      }),
      {},
    ),
    ...requiredFields?.reduce<Record<string, { required: boolean }>>(
      (res, item) => ({
        ...res,
        [item]: { required: true },
      }),
      {},
    ),
  };

  const taxSystemOptions = [
    {
      key: "USN",
      label: message({
        id: "12774644-c4e0-4811-95aa-eda29b7386fa",
        context:
          "Drivers. Drivers form. Details group. Tax system option (USN)",
        default: "Simplified tax system (USN)",
      }),
    },
    {
      key: "USNP",
      label: message({
        id: "8902d5a0-2ffc-4741-bef4-9696d47c4378",
        context:
          "Drivers. Drivers form. Details group. Tax system option (USNP)",
        default: "Patent tax system (with Simplified taxation)",
      }),
    },
    {
      key: "OSNP",
      label: message({
        id: "5617ac53-726a-4883-be2e-c5bfbe0e6103",
        context:
          "Drivers. Drivers form. Details group. Tax system option (OSNP)",
        default: "Patent tax system (with General taxation)",
      }),
    },
    {
      key: "AUSN",
      label: message({
        id: "3c19c057-b56c-4876-a2dc-91e290420be6",
        context:
          "Drivers. Drivers form. Details group. Tax system option (AUTOUSN)",
        default: "Automated simplified tax system (AUSN)",
      }),
    },
    {
      key: "OSN",
      label: message({
        id: "cfd866c2-bc4c-4fbf-8b37-c7f2a7cd5c24",
        context:
          "Drivers. Drivers form. Details group. Tax system option (OSN)",
        default: "General tax system (OSN)",
      }),
    },
  ];

  const countryCode = getCountryCallingCode(
    parkInfo.country_code as CountryCode,
  );

  const [personalState, setPersonalState] = useState<PersonalFormState>(() => ({
    phone: createPhoneFieldState({
      value: countryCode ? "+" + countryCode : "",
      errorMessage: countryCode
        ? errorMessages.invalidPhone
        : errorMessages.requiredField,
    }),
    first_name: createTextFieldState({
      value: "",
      errorMessage: errorMessages.requiredField,
    }),
    last_name: createTextFieldState({
      value: "",
      errorMessage: errorMessages.requiredField,
    }),
    address: createComboBoxState({
      errorMessage: additionalFieldsMap["address"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    address_apartment: createTextFieldState({
      value: "",
      errorMessage: additionalFieldsMap["address_apartment"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    doc_address: createComboBoxState({
      errorMessage: additionalFieldsMap["doc_address"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    tax_identification_number: createTextFieldState({
      value: "",
      errorMessage: additionalFieldsMap["tax_identification_number"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    tax_system: createComboBoxState({
      errorMessage: additionalFieldsMap["tax_system"]
        ? errorMessages.requiredField
        : undefined,
    }),
    email: createTextFieldState({
      value: "",
      errorMessage: additionalFieldsMap["email"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    tax_authority_code: createTextFieldState({
      value: "",
      errorMessage: additionalFieldsMap["tax_authority_code"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    station_id: createComboBoxState({
      errorMessage: additionalFieldsMap["station_id"]
        ? errorMessages.requiredField
        : undefined,
    }),
  }));

  const [regionState, setRegionState] = useState<RegionDetailsState>(() => ({
    region: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
  }));

  const onShowRegionErrors = useOnShowErrors(setRegionState);

  const [confirmationId, setConfirmationId] = useState("");

  const debouncedStationState = useDebouncedState(
    personalState.station_id,
    300,
  );

  const enableCountrySelection = flag({
    id: "994de453-45f8-442b-a47d-fe42d9fb8a61",
    description: "Enable driver license country selection",
  });

  const [licenseState, setLicenseState] = useState<DriverLicenseDetailsState>(
    () => ({
      country: createComboBoxState(
        enableCountrySelection
          ? {
              value: undefined,
              errorMessage: errorMessages.requiredField,
            }
          : undefined,
      ),
      number: createTextFieldState({
        value: "",
        errorMessage: errorMessages.requiredField,
      }),
      issue_date: createDateFieldState({
        errorMessage: errorMessages.requiredField,
      }),
      expiry_date: createDateFieldState({
        errorMessage: errorMessages.requiredField,
      }),
    }),
  );

  const onChangeLicensePartial = useOnChangePartial(setLicenseState);
  const onShowLicenseErrors = useOnShowErrors(setLicenseState);

  const [licenseTemplates, setLicenseTemplates] = useState<string[]>(
    parkInfo.license_number_templates ?? [],
  );

  const { action: getLicenseTemplatesData } = useAction({
    url: "/fleet-forms/selfreg/v1/license/number-templates",
    method: "GET",
  });

  const { data: stationData } = useOnDemandV1StationsGet(
    {
      query: {
        ref_id: refId,
        ...(debouncedStationState.inputValue && {
          text_query: debouncedStationState.inputValue,
        }),
      },
      headers: {
        language: locale.toString(),
      },
    },
    {
      enabled: !!additionalFieldsMap["station_id"],
    },
  );

  const stationOptions =
    stationData?.data.stations.map((item) => ({
      key: item.id,
      label: item.taxi_station_name,
    })) ?? [];

  const onChangePersonalPartial = useOnChangePartial(setPersonalState);
  const onShowPersonalErrors = useOnShowErrors(setPersonalState);

  const { action: actionSubmit, isFetching } = useAction({
    url: "/fleet-forms/selfreg/v1/submit",
    method: "POST",
  });

  const { validateTextField, validateComboboxField, validatePhoneField } =
    useValidate();

  const optionalFieldDescription = message({
    id: "b510cd8b-df55-4718-8234-e6d43399cbaf",
    context: "Yango forms. Optional field description",
    default: "Optional",
  });

  const isAddressFieldsDisabled = enablePhoneVerification && !confirmationId;

  const submitErrorMessages = useSubmitErrorMessages(parkInfo.country_code);
  const onError = (code?: string) => {
    if (code) {
      setFormErrorMessage(submitErrorMessages[code] ?? errorMessages.default);
    } else {
      setFormErrorMessage(errorMessages.default);
    }
  };

  return (
    <RegisterForm
      isIframe={isIframe}
      content={
        <>
          <ExperimentalFormRow columnsCount={enablePhoneVerification ? 2 : 1}>
            <PhoneField
              required
              label={message({
                id: "37cdb078-34d7-4241-b640-60984eb9c937",
                context: "Yango forms. Phone number field",
                default: "Phone number",
              })}
              state={personalState.phone}
              onChange={(state) => {
                setConfirmationId("");
                const errorMessage = validatePhoneField(state.value, {
                  isPhoneInvalid: enableTestPhones
                    ? state.invalid && !state.value.match(/^\+7000[0-9]{7}$/)
                    : state.invalid,
                });
                onChangePersonalPartial({
                  phone: {
                    ...state,
                    errorMessage,
                  },
                });
              }}
              {...(employmentType === "selfemployed" &&
              nextStep !== "details" &&
              parkInfo.country_code === "RU"
                ? {
                    description: message({
                      id: "d3c49ca2-998f-4017-b53a-8774a5f4c452",
                      context: "Yandex forms. Phone number field",
                      default:
                        "The contractor's phone number must be the same as the one used to register in My Taxes",
                    }),
                  }
                : {})}
            />
            {enablePhoneVerification && (
              <PhoneVerificator
                phone={
                  !personalState.phone.invalid &&
                  !personalState.phone.errorMessage
                    ? personalState.phone.value
                    : ""
                }
                state={confirmationId}
                onChange={setConfirmationId}
              />
            )}
          </ExperimentalFormRow>
          <ExperimentalFormRow columnsCount={2}>
            <TextField
              required
              state={personalState.first_name}
              label={message({
                id: "b3ba4ff6-287c-4ab4-84fc-68d508d18be1",
                context: "Yango form. Name field",
                default: "First name",
              })}
              onChange={(state) => {
                const errorMessage = validateTextField(state.value);
                onChangePersonalPartial({
                  first_name: {
                    ...state,
                    errorMessage,
                    errorVisible: !!errorMessage,
                  },
                });
              }}
            />
            <TextField
              required
              state={personalState.last_name}
              label={message({
                id: "c74fa278-7362-4a3f-af30-fd5bd697c55e",
                context: "Yango forms. Surname field",
                default: "Last name",
              })}
              onChange={(state) => {
                const errorMessage = validateTextField(state.value);
                onChangePersonalPartial({
                  last_name: {
                    ...state,
                    errorMessage,
                    errorVisible: !!errorMessage,
                  },
                });
              }}
            />
          </ExperimentalFormRow>
          {Object.keys(additionalFieldsMap).length > 0 &&
            !additionalFieldsMap["station_id"] && (
              <span
                css={{
                  color: uiColors.text,
                  ...typo({
                    level: "body2",
                    weight: "medium",
                    density: "tight",
                  }),
                  ...cssFns.padding("0", "16px"),
                }}
              >
                <Message
                  id="120ba688-e8b6-4dcc-914f-fea142ef7aa5"
                  context="Yango forms. Additional fields title"
                  default="Required for receipts"
                />
              </span>
            )}
          {additionalFieldsMap["address"] && (
            <ExperimentalFormRow columnsCount={2}>
              <AddressSuggest
                refId={refId}
                phone={personalState.phone.value}
                disabled={isAddressFieldsDisabled}
                required={additionalFieldsMap["address"].required}
                label={message({
                  id: "75ed82d9-4d27-4898-a531-2660daf6e3d2",
                  context: "Yango forms. Actual adress field",
                  default: "Current address",
                })}
                description={
                  additionalFieldsMap["address"].required
                    ? undefined
                    : optionalFieldDescription
                }
                confirmationId={confirmationId}
                enablePhoneVerification={enablePhoneVerification}
                state={personalState.address}
                onChange={(state) => {
                  const errorMessage = additionalFieldsMap["address"]?.required
                    ? validateComboboxField(state)
                    : undefined;

                  onChangePersonalPartial({
                    address: {
                      ...state,
                      errorMessage,
                      errorVisible: Boolean(errorMessage),
                    },
                  });
                }}
              />
              <TextField
                required={additionalFieldsMap["address_apartment"]?.required}
                state={personalState.address_apartment}
                disabled={isAddressFieldsDisabled}
                label={message({
                  id: "0534f756-b7ed-450c-b095-9b78100b900a",
                  context: "Yango forms. Actual adress apartment field",
                  default: "Apartment",
                })}
                description={
                  additionalFieldsMap["address_apartment"]?.required
                    ? undefined
                    : optionalFieldDescription
                }
                onChange={(state) => {
                  onChangePersonalPartial({
                    address_apartment: {
                      ...state,
                    },
                  });
                }}
              />
            </ExperimentalFormRow>
          )}
          {additionalFieldsMap["doc_address"] && (
            <ExperimentalFormRow columnsCount={2}>
              <AddressSuggest
                refId={refId}
                phone={personalState.phone.value}
                confirmationId={confirmationId}
                enablePhoneVerification={enablePhoneVerification}
                disabled={isAddressFieldsDisabled}
                required={additionalFieldsMap["doc_address"].required}
                label={message({
                  id: "c68cb51a-1a0f-4ba5-9513-9a2476501a9f",
                  context: "Yango forms. Registration adress field",
                  default: "Registration address",
                })}
                description={
                  additionalFieldsMap["doc_address"].required
                    ? undefined
                    : optionalFieldDescription
                }
                state={personalState.doc_address}
                onChange={(state) => {
                  const errorMessage = additionalFieldsMap["doc_address"]
                    ?.required
                    ? validateComboboxField(state)
                    : undefined;

                  onChangePersonalPartial({
                    doc_address: {
                      ...state,
                      errorMessage,
                      errorVisible: Boolean(errorMessage),
                    },
                  });
                }}
              />
            </ExperimentalFormRow>
          )}
          {(additionalFieldsMap["tax_identification_number"] ||
            additionalFieldsMap["tax_system"]) && (
            <ExperimentalFormRow columnsCount={2}>
              {additionalFieldsMap["tax_identification_number"] && (
                <TextField
                  required={
                    additionalFieldsMap["tax_identification_number"].required
                  }
                  state={personalState.tax_identification_number}
                  label={
                    parkInfo.country_code === "UZ"
                      ? message({
                          id: "5dac1327-b91c-439a-8990-0af9d57e03a8",
                          context: "Register form. Identification number",
                          default: "Private entity ID",
                        })
                      : parkInfo.country_code === "MD"
                        ? message({
                            id: "f0aa9505-82f2-4224-acc9-6340b3221689",
                            context:
                              "Register form. Identification number in Moldova",
                            default: "IDNP",
                          })
                        : message({
                            id: "a6d3cfda-ef03-4ec9-994c-73c9f041ec68",
                            context: "Yango forms. Taxpayer ID field",
                            default: "Taxpayer ID",
                          })
                  }
                  description={
                    additionalFieldsMap["tax_identification_number"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap[
                      "tax_identification_number"
                    ]?.required
                      ? validateTextField(state.value)
                      : undefined;
                    onChangePersonalPartial({
                      tax_identification_number: {
                        ...state,
                        errorMessage,
                        errorVisible: !!errorMessage,
                      },
                    });
                  }}
                />
              )}
              {additionalFieldsMap["tax_system"] && (
                <FormSuggest
                  required={additionalFieldsMap["tax_system"].required}
                  data={taxSystemOptions}
                  label={message({
                    id: "fd69425d-61a4-4ac6-8fbb-cd2aefe4517c",
                    context: "Yango forms. Tax system field",
                    default: "Tax system",
                  })}
                  description={
                    additionalFieldsMap["tax_system"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  state={personalState.tax_system}
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap["tax_system"]
                      ?.required
                      ? validateComboboxField(state)
                      : undefined;
                    onChangePersonalPartial({
                      tax_system: {
                        ...state,
                        errorMessage,
                        errorVisible: Boolean(errorMessage),
                      },
                    });
                  }}
                />
              )}
            </ExperimentalFormRow>
          )}
          {(additionalFieldsMap["email"] ||
            additionalFieldsMap["tax_authority_code"] ||
            additionalFieldsMap["station_id"]) && (
            <ExperimentalFormRow columnsCount={2}>
              {additionalFieldsMap["email"] && (
                <TextField
                  required={additionalFieldsMap["email"].required}
                  state={personalState.email}
                  label={message({
                    id: "ef937bc1-e96e-4258-8023-3eb9130cfa83",
                    context: "Yango forms. Email field",
                    default: "Email",
                  })}
                  description={
                    additionalFieldsMap["email"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap["email"]?.required
                      ? validateTextField(state.value)
                      : undefined;
                    onChangePersonalPartial({
                      email: {
                        ...state,
                        errorMessage,
                        errorVisible: !!errorMessage,
                      },
                    });
                  }}
                />
              )}
              {additionalFieldsMap["tax_authority_code"] && (
                <TextField
                  required={additionalFieldsMap["tax_authority_code"].required}
                  state={personalState.tax_authority_code}
                  label={message({
                    id: "f4e0a9db-5032-4f20-8a22-c733e9147bb8",
                    context: "Yango forms. Tax authority code field",
                    default: "Tax authority code",
                  })}
                  description={
                    additionalFieldsMap["tax_authority_code"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap[
                      "tax_authority_code"
                    ]?.required
                      ? validateTextField(state.value)
                      : undefined;
                    onChangePersonalPartial({
                      tax_authority_code: {
                        ...state,
                        errorMessage,
                        errorVisible: !!errorMessage,
                      },
                    });
                  }}
                />
              )}
              {additionalFieldsMap["station_id"] && (
                <FormSuggest
                  required={additionalFieldsMap["station_id"].required}
                  data={stationOptions}
                  label={message({
                    id: "f8cef533-c355-4136-835c-44a13bd50f62",
                    context: "Yango forms. Station field",
                    default: "Taxi station",
                  })}
                  description={
                    additionalFieldsMap["station_id"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  state={personalState.station_id}
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap["station_id"]
                      ?.required
                      ? validateComboboxField(state)
                      : undefined;
                    onChangePersonalPartial({
                      station_id: {
                        ...state,
                        errorMessage,
                        errorVisible: Boolean(errorMessage),
                      },
                    });
                  }}
                />
              )}
            </ExperimentalFormRow>
          )}
          {hasRegionSection && (
            <RegionDetailsFields
              state={regionState}
              onChange={setRegionState}
              refId={refId}
            />
          )}
          {hasLicenseSection && (
            <>
              <div
                css={{
                  color: uiColors.text,
                  ...typo({
                    level: "body2",
                    density: "normal",
                    weight: "medium",
                  }),
                  ...cssFns.padding("0", "16px"),
                }}
              >
                <Message
                  id="b57955b1-087c-4224-8f38-9e6e566bf2a9"
                  context="Forms. License details section. Title"
                  default="Driver's license"
                />
              </div>
              <DriverLicenseDetailsFields
                state={licenseState}
                onChangePartial={onChangeLicensePartial}
                countryCode={parkInfo.country_code}
                countryId={parkInfo.country_id}
                licenseTemplates={licenseTemplates}
              />
            </>
          )}
        </>
      }
      onSubmit={() => {
        if (hasLicenseSection) {
          onShowLicenseErrors();
        }

        const hasLicenseErrors =
          hasLicenseSection &&
          Object.keys(licenseState).some(
            (field) =>
              licenseState[field as keyof DriverLicenseDetailsState]
                .errorMessage !== undefined,
          );

        if (hasLicenseErrors) {
          if (
            (licenseTemplates.length > 0 &&
              licenseState.number.errorMessage ===
                errorMessages.invalidDriverLicenseWithTemplates(
                  licenseTemplates.join(", "),
                )) ||
            licenseState.number.errorMessage ===
              errorMessages.invalidDriverLicense
          ) {
            logActionInvalid("invalid_driver_license", {
              onlyYM: true,
            });
          }

          if (
            licenseState.expiry_date.errorMessage === errorMessages.invalidDate
          ) {
            logActionInvalid("invalid_expiry_date", {
              onlyYM: true,
            });
          }

          if (
            licenseState.issue_date.errorMessage === errorMessages.invalidDate
          ) {
            logActionInvalid("invalid_issue_date", {
              onlyYM: true,
            });
          }
        }

        if (hasRegionSection) {
          onShowRegionErrors();
        }

        const hasRegionErrors =
          hasRegionSection &&
          Object.keys(regionState).some(
            (field) =>
              regionState[field as keyof RegionDetailsState].errorMessage !==
              undefined,
          );

        onShowPersonalErrors();

        const hasErrors = Object.keys(personalState).some(
          (field) =>
            personalState[field as keyof PersonalFormState].errorMessage !==
            undefined,
        );

        if (hasErrors) {
          if (personalState.phone.errorMessage === errorMessages.invalidPhone) {
            logActionInvalid("invalid_phone_number", {
              onlyYM: true,
            });
          }
        }

        if (hasErrors || hasLicenseErrors || hasRegionErrors) {
          return;
        }

        const personal = {
          first_name: personalState.first_name.value,
          last_name: personalState.last_name.value,
          phone: personalState.phone.value,
          ref_id: refId,
          agree_processing_data: true,
          agree_receive_info: true,
          address: personalState.address.value?.key,
          address_apartment: sendValueOrUndefined(
            personalState.address_apartment.value,
          ),
          doc_address: personalState.doc_address.value?.key,
          tax_identification_number: sendValueOrUndefined(
            personalState.tax_identification_number.value,
          ),
          tax_system: personalState.tax_system.value?.key,
          tax_authority_code: sendValueOrUndefined(
            personalState.tax_authority_code.value,
          ),
          email: sendValueOrUndefined(personalState.email.value),
          confirmation_id: sendValueOrUndefined(confirmationId),
          station_id: personalState.station_id.value?.key,
          profession_id: profession?.profession_id,
          employment_type: employmentType,
        } as const;

        const license =
          licenseState.issue_date.value && licenseState.expiry_date.value
            ? {
                number: licenseState.number.value,
                issue_date: licenseState.issue_date.value.toString(),
                expiry_date: licenseState.expiry_date.value.toString(),
                country_id:
                  licenseState.country.value?.key ?? parkInfo.country_id,
              }
            : undefined;

        const park = regionState.region.value
          ? {
              region_id: regionState.region.value.key,
            }
          : undefined;

        actionSubmit(
          {
            headers: {
              "x-submission-id": sessionId,
              language: locale.toString(),
              "content-type": "application/json",
            },
            query: {
              ref_id: refId,
            },
            body: {
              personal,
              ...(hasLicenseSection && {
                license,
              }),
              ...(hasRegionSection && {
                park,
              }),
            },
          },
          {
            onResponse: (res) => {
              if (res.status === 204) {
                logActionSubmit("submit_form", {
                  params: "no_auth",
                });
                const searchParams = new URLSearchParams({
                  ref_id: refId,
                  ...(profession
                    ? { profession_id: profession.profession_id }
                    : undefined),
                  ...(employmentType
                    ? { employment_type_id: employmentType }
                    : undefined),
                  ...(sessionId ? { sessionId } : undefined),
                });

                navigate(`/${nextStep ?? "download-app"}?${searchParams}`);
                return;
              }

              switch (res.body.code) {
                case "invalid_phone_number": {
                  logActionInvalid("invalid_phone_number", {
                    onlyYM: true,
                  });
                  onChangePersonalPartial({
                    phone: {
                      ...personalState.phone,
                      errorMessage: errorMessages.invalidPhone,
                      errorVisible: true,
                    },
                  });
                  break;
                }
                case "invalid_driver_license": {
                  logActionInvalid("invalid_driver_license", {
                    onlyYM: true,
                  });
                  if (enableCountrySelection) {
                    getLicenseTemplatesData(
                      {
                        headers: { language: locale.toString() },
                        query: {
                          country_id:
                            licenseState.country.value?.key ??
                            parkInfo.country_id,
                        },
                      },
                      {
                        onResponse: (res) => {
                          let templates: string[] = [];
                          if (res.status === 200) {
                            setLicenseTemplates(
                              res.body.license_number_templates,
                            );
                            templates = res.body.license_number_templates;
                          }
                          onChangeLicensePartial({
                            number: {
                              ...licenseState.number,
                              errorMessage:
                                templates.length > 0
                                  ? errorMessages.invalidDriverLicenseWithTemplates(
                                      templates.join(", "),
                                    )
                                  : errorMessages.invalidDriverLicense,
                              errorVisible: true,
                            },
                          });
                        },
                      },
                    );
                  } else {
                    onChangeLicensePartial({
                      number: {
                        ...licenseState.number,
                        errorMessage:
                          licenseTemplates.length > 0
                            ? errorMessages.invalidDriverLicenseWithTemplates(
                                licenseTemplates.join(", "),
                              )
                            : errorMessages.invalidDriverLicense,
                        errorVisible: true,
                      },
                    });
                  }
                  break;
                }
                case "invalid_issue_date": {
                  logActionInvalid("invalid_issue_date", { onlyYM: true });
                  onChangeLicensePartial({
                    issue_date: {
                      ...licenseState.issue_date,
                      errorMessage: errorMessages.invalidDate,
                      errorVisible: true,
                    },
                  });
                  break;
                }
                case "invalid_expiry_date": {
                  logActionInvalid("invalid_expiry_date", {
                    onlyYM: true,
                  });
                  onChangeLicensePartial({
                    expiry_date: {
                      ...licenseState.expiry_date,
                      errorMessage: errorMessages.invalidDate,
                      errorVisible: true,
                    },
                  });
                  break;
                }
                default: {
                  onError(res.body.code);
                  break;
                }
              }
              return;
            },
          },
        );
      }}
      isLoading={isFetching}
      isDelivery={isDelivery}
      formErrorMessage={formErrorMessage}
      setFormErrorMessage={setFormErrorMessage}
      parkInfo={parkInfo}
    />
  );
};

const sendValueOrUndefined = (value: string) => {
  return value.length > 0 ? value : undefined;
};
